import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  TextArea,
  Skeleton,
  ErrorBoundary,
  FileUpload,
  useToast,
} from '@bbpos/ui';
import { uploadPurchaseDocument, DocumentUploadError, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES } from '@/features/vendor-bills';
import { GLASS_FORM } from '@/constants/design-tokens';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { docCurrencySymbol, formatDocAmount, isValidExchangeRate, toTenantAmount } from '@/utils/purchase-currency';
import { usePurchaseOrder, useReceivePO, POStatusBadge, parseSerials, serialError } from '@/features/purchase-orders';
import type { PurchaseOrderItem } from '@/features/purchase-orders';
import type { TrackingMode } from '@/types/product';
import { ArrowLeftIcon, TruckIcon, CheckIcon, CalendarIcon, PackageIcon } from './icons';
import { SubstituteEditor, emptySubstitute, substituteError } from './SubstituteEditor';
import type { SubstituteDraft } from './SubstituteEditor';

// ─── Receive item row ───────────────────────────────────────────────────────

interface ReceiveRow {
  po_item_id: string;
  product_name: string;
  ordered: number;
  already_received: number;
  remaining: number;
  unit: string;
  unit_cost: number;
  /** unit_cost net of the line discount, spread over the ORDERED quantity — the API's fallback cost. */
  net_unit_cost: number;
  quantity_to_receive: string;
  actual_unit_cost: string;
  batch_number: string;
  expiry_date: string;
  /** Raw serial-number text (one per line / comma separated) for SERIAL lines. */
  serials_text: string;
  /** Resolved server-side: NONE | BATCH | SERIAL (absent → untracked). */
  tracking: TrackingMode;
  notes: string;
}

const fmtQty = (n: number) => n.toFixed(3).replace(/\.?0+$/, '');

// ─── Progress bar ───────────────────────────────────────────────────────────

function ReceiveProgressBar({
  ordered,
  received,
  receiving,
}: {
  ordered: number;
  received: number;
  receiving: number;
}) {
  // Over-receipt is allowed, so the bar is scaled to the larger of ordered and
  // (received + receiving) and never overflows; the label says how far over.
  const total = received + receiving;
  const scale = Math.max(ordered, total);
  const receivedPct = scale > 0 ? (received / scale) * 100 : 0;
  const receivingPct = scale > 0 ? (receiving / scale) * 100 : 0;
  const overBy = ordered > 0 ? total - ordered : 0;
  return (
    <div className="w-full">
      <div className="flex h-3 rounded-full bg-neutral-200/60 dark:bg-neutral-700/60 overflow-hidden">
        {receivedPct > 0 && (
          <div
            className="bg-success-500 transition-all duration-300"
            style={{ width: `${receivedPct}%` }}
          />
        )}
        {receivingPct > 0 && (
          <div
            className={`${overBy > 0 ? 'bg-warning-400 dark:bg-warning-500' : 'bg-teal-400 dark:bg-teal-500'} transition-all duration-300`}
            style={{ width: `${receivingPct}%` }}
          />
        )}
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-neutral-500 dark:text-neutral-400">
        {receivedPct > 0 && (
          <span className="flex items-center gap-1 mono-num">
            <span className="h-2 w-2 rounded-full bg-success-500" />
            Received: {received}
          </span>
        )}
        {receivingPct > 0 && (
          <span className="flex items-center gap-1 mono-num">
            <span className="h-2 w-2 rounded-full bg-teal-400" />
            Receiving: {receiving}
          </span>
        )}
        {ordered - total > 0 && (
          <span className="flex items-center gap-1 mono-num">
            <span className="h-2 w-2 rounded-full bg-neutral-300" />
            Remaining: {fmtQty(ordered - total)}
          </span>
        )}
        {overBy > 0 && (
          <span className="flex items-center gap-1 mono-num text-warning-700 dark:text-warning-300 font-medium">
            <span className="h-2 w-2 rounded-full bg-warning-400" />
            {Math.round((total / ordered) * 100)}% of ordered (+{fmtQty(overBy)})
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function PurchaseOrderReceiveContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const tenantCurrency = useTenantCurrency();
  const receiveMutation = useReceivePO();

  const { data: po, isLoading, error } = usePurchaseOrder(id);
  // Costs are typed in the PO's own currency; a foreign one reaches stock in the
  // tenant currency at the order's rate (docs/multi-currency-purchasing-plan.md D2).
  const currency = po?.currency_code ?? tenantCurrency;
  const isForeignCurrency = !!po?.currency_code;
  // The rate may still change on the first receipt; after a receipt or a bill it is fixed (D3).
  const rateEditable = isForeignCurrency
    && (po?.receiving_history?.length ?? 0) === 0
    && !(po?.vendor_bills ?? []).some((b) => b.status !== 'CANCELLED');
  const [exchangeRate, setExchangeRate] = useState('');
  useEffect(() => {
    setExchangeRate(po?.exchange_rate ? po.exchange_rate.replace(/\.?0+$/, '') : '');
  }, [po?.exchange_rate]);

  const [rows, setRows] = useState<ReceiveRow[]>([]);
  // POS comments item 10: an item the supplier sent instead of a line, by line id.
  const [substitutes, setSubstitutes] = useState<Record<string, SubstituteDraft>>({});
  const [receivedDate, setReceivedDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  // Vendor invoice handed over with the goods (optional): number, date and the
  // file. The file is uploaded on submit; a failed upload blocks the receipt so
  // we never record "invoice attached" without the file.
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Populate rows when PO loads. Fully received lines stay receivable — vendors
  // may deliver extras (over-receipt is allowed while the PO is not RECEIVED).
  useEffect(() => {
    if (!po?.items) return;
    setRows(
      po.items
        // A line closed short is finished — nothing more is expected for it.
        .filter((item: PurchaseOrderItem) => item.status !== 'CLOSED')
        .map((item: PurchaseOrderItem) => {
          // Same rule as the API's receive fallback, so leaving the field as offered values
          // the stock ex-discount (QA 6.4.13); typing a cost still overrides it (6.4.14).
          const netUnitCost = item.line_discount > 0 && item.quantity > 0
            ? Math.floor((item.total_cost - item.line_discount) / item.quantity)
            : item.unit_cost;
          return {
            po_item_id: item.id,
            product_name: item.product_name,
            ordered: item.quantity,
            already_received: item.received_quantity,
            remaining: Math.max(0, item.quantity - item.received_quantity),
            unit: item.unit,
            unit_cost: item.unit_cost,
            net_unit_cost: netUnitCost,
            quantity_to_receive: '',
            actual_unit_cost: (netUnitCost / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)),
            batch_number: '',
            expiry_date: '',
            serials_text: '',
            tracking: item.tracking ?? 'NONE',
            notes: '',
          };
        }),
    );
  }, [po]);

  function updateRow(
    itemId: string,
    field: keyof Pick<ReceiveRow, 'quantity_to_receive' | 'actual_unit_cost' | 'batch_number' | 'expiry_date' | 'serials_text' | 'notes'>,
    value: string,
  ) {
    setRows((prev) =>
      prev.map((r) => (r.po_item_id === itemId ? { ...r, [field]: value } : r)),
    );
  }

  const hasValidItems = useMemo(
    () => rows.some((r) => parseFloat(r.quantity_to_receive) > 0) || Object.keys(substitutes).length > 0,
    [rows, substitutes],
  );

  const validationErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    rows.forEach((r) => {
      const qty = parseFloat(r.quantity_to_receive);
      if (qty > 0) {
        const actual = parseFloat(r.actual_unit_cost);
        if (!Number.isFinite(actual) || actual < 0) {
          errs[r.po_item_id + '__cost'] = 'Actual unit cost must be ≥ 0';
        }
        if (r.tracking === 'SERIAL') {
          const err = serialError(parseSerials(r.serials_text), qty);
          if (err) errs[r.po_item_id + '__serial'] = err;
        } else if (r.tracking === 'BATCH') {
          // Batch + expiry must travel together
          if ((r.batch_number && !r.expiry_date) || (!r.batch_number && r.expiry_date)) {
            errs[r.po_item_id + '__batch'] = 'Provide both batch # and expiry, or neither';
          }
        }
      }
    });
    for (const [itemId, sub] of Object.entries(substitutes)) {
      const err = substituteError(sub);
      if (err) errs[itemId + '__substitute'] = err;
    }
    return errs;
  }, [rows, substitutes]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleSubmit = useCallback(async () => {
    if (!id || hasErrors) return;

    const itemsToReceive = rows
      .filter((r) => parseFloat(r.quantity_to_receive) > 0)
      .map((r) => {
        const actualRupees = parseFloat(r.actual_unit_cost);
        return {
          po_item_id: r.po_item_id,
          quantity_received: parseFloat(r.quantity_to_receive),
          actual_unit_cost: Number.isFinite(actualRupees)
            ? Math.round(actualRupees * getSubunitMultiplier(currency))
            : undefined,
          batch_number: r.tracking === 'BATCH' ? r.batch_number.trim() || undefined : undefined,
          expiry_date: r.tracking === 'BATCH' ? r.expiry_date || undefined : undefined,
          serial_numbers: r.tracking === 'SERIAL' ? parseSerials(r.serials_text) : undefined,
          notes: r.notes.trim() || undefined,
        };
      });

    const substitutesToReceive = Object.entries(substitutes).map(([itemId, sub]) => ({
      replaces_po_item_id: itemId,
      product_id: sub.product_id,
      quantity: parseFloat(sub.quantity),
      ...(sub.unit_cost.trim() ? { unit_cost: Math.round(parseFloat(sub.unit_cost) * getSubunitMultiplier(currency)) } : {}),
      close_original: sub.close_original,
    }));

    if (itemsToReceive.length === 0 && substitutesToReceive.length === 0) {
      toast({ title: 'Enter quantity for at least one item', variant: 'error' });
      return;
    }

    let document: { file_key: string; file_name?: string; content_type?: string; size_bytes?: number } | undefined;
    if (invoiceFile) {
      setUploading(true);
      try {
        document = await uploadPurchaseDocument(invoiceFile);
      } catch (err) {
        setUploading(false);
        toast({ title: err instanceof DocumentUploadError ? err.message : 'Invoice upload failed', variant: 'error' });
        return;
      }
      setUploading(false);
    }

    const rateChanged = rateEditable && Number(exchangeRate) !== Number(po?.exchange_rate);
    if (rateChanged && !isValidExchangeRate(exchangeRate)) {
      toast({ title: `Enter how much ${tenantCurrency} 1 ${currency} is (up to 8 decimals)`, variant: 'error' });
      return;
    }

    receiveMutation.mutate(
      {
        id,
        payload: {
          items: itemsToReceive,
          ...(rateChanged ? { exchange_rate: exchangeRate.trim() } : {}),
          ...(substitutesToReceive.length ? { substitutes: substitutesToReceive } : {}),
          received_date: receivedDate || undefined,
          vendor_invoice_number: invoiceNumber.trim() || undefined,
          vendor_invoice_date: invoiceDate || undefined,
          document,
        },
      },
      {
        onSuccess: (result) => {
          const totalReceived = itemsToReceive.reduce((s, i) => s + i.quantity_received, 0)
            + substitutesToReceive.reduce((s, i) => s + i.quantity, 0);
          const grn = (result as { grn_number?: string | null }).grn_number;
          toast({
            title: `Received ${itemsToReceive.length + substitutesToReceive.length} item(s) (${totalReceived} units) for ${po?.po_number}${grn ? ` · ${grn}` : ''}`,
            variant: 'success',
          });
          // Back to the PO — its Receipts card offers "Bill this receipt" for
          // the GRN when the vendor's invoice came with the goods.
          navigate(`/inventory/purchase-orders/${id}`);
        },
        onError: (err: Error) => {
          toast({ title: err.message, variant: 'error' });
        },
      },
    );
  }, [id, rows, substitutes, receivedDate, invoiceNumber, invoiceDate, invoiceFile, hasErrors, receiveMutation, navigate, toast, po, currency, rateEditable, exchangeRate, tenantCurrency]);

  // Keyboard: Ctrl+Enter to submit, Esc to back
  // Escape leaves the receipt; ask first once anything has been entered.
  const formDirty =
    rows.some((r) => r.quantity_to_receive !== '') ||
    Object.keys(substitutes).length > 0 ||
    invoiceNumber.trim() !== '' ||
    invoiceFile !== null;
  const { confirmLeave, dialog: leaveDialog } = useUnsavedChangesGuard(formDirty);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && hasValidItems && !hasErrors) {
        e.preventDefault();
        void handleSubmit();
      }
      // A dropdown that was open handled this Escape (closed itself).
      if (e.key === 'Escape' && e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        confirmLeave(() => navigate(`/inventory/purchase-orders/${id}`));
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleSubmit, hasValidItems, hasErrors, navigate, id, confirmLeave]);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/inventory/purchase-orders');
    return null;
  }

  if (isLoading || !po) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-2xl mb-6" />
        <Skeleton variant="rect" height={420} className="rounded-2xl" />
      </>
    );
  }

  if (po.status !== 'ORDERED' && po.status !== 'PARTIALLY_RECEIVED') {
    toast({ title: `Cannot receive goods — PO is ${po.status.toLowerCase()}`, variant: 'error' });
    navigate(`/inventory/purchase-orders/${id}`);
    return null;
  }

  const totalLines = rows.filter((r) => parseFloat(r.quantity_to_receive) > 0).length;
  const totalUnits = rows.reduce((s, r) => s + (parseFloat(r.quantity_to_receive) || 0), 0);

  return (
    <>
      {/* Gradient header — fintech weight */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white/15 p-2 backdrop-blur-sm ring-1 ring-white/20">
                <TruckIcon className="h-5 w-5" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-white">Receive Goods</h1>
            </div>
            <p className="text-teal-100 mt-1 mono-num">
              {po.po_number} <span className="text-teal-200/70">·</span> {po.supplier_name}
            </p>
            {/* Stock lands at the PO's location, whoever receives it (QA 6.4.8). */}
            <p className="text-sm text-white/90 mt-1">
              Receiving into <span className="font-semibold text-white">{po.location_name}</span>
            </p>
          </div>
          <POStatusBadge status={po.status} size="md" />
        </div>
      </div>

      {/* Receiving form */}
      <div className={`${GLASS_FORM} p-6 animate-slide-up`}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
              Items to Receive
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 mono-num">
              {totalLines} of {rows.length} lines · {totalUnits.toFixed(3).replace(/\.?0+$/, '')} units
            </p>
          </div>
          <div className="w-44">
            <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
              Received Date
            </label>
            <Input type="date" value={receivedDate} onChange={(v) => setReceivedDate(v)} />
          </div>
        </div>

        {isForeignCurrency && (
          <div className="mb-5 rounded-xl bg-brand-50 dark:bg-brand-900/20 px-4 py-3 text-sm text-brand-900 dark:text-brand-100 flex flex-wrap items-end gap-4">
            <div className="w-56">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-brand-700 dark:text-brand-300 mb-1">
                Exchange rate: 1 {currency} in {tenantCurrency}
              </label>
              <Input
                value={exchangeRate}
                inputMode="decimal"
                disabled={!rateEditable}
                onChange={(v) => setExchangeRate(v.trim())}
              />
            </div>
            <p className="text-xs flex-1 min-w-[12rem]">
              Costs are in {currency}. Stock is costed in {tenantCurrency} at this rate.{' '}
              {rateEditable
                ? 'You can still change it on this first receipt.'
                : 'It is fixed now that goods were received or a bill was raised.'}
            </p>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-12">
            This purchase order has no lines to receive.
          </p>
        ) : (
          <div className="space-y-5">
            {rows.map((row) => {
              const qty = parseFloat(row.quantity_to_receive) || 0;
              const costErr = validationErrors[row.po_item_id + '__cost'];
              const batchErr = validationErrors[row.po_item_id + '__batch'];
              const serialErr = validationErrors[row.po_item_id + '__serial'];
              const serialCount = row.tracking === 'SERIAL' ? parseSerials(row.serials_text).length : 0;
              const overBy = qty > row.remaining ? qty - row.remaining : 0;
              return (
                <div
                  key={row.po_item_id}
                  className="relative pl-5 before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-teal-400 before:to-teal-600 rounded-xl bg-white/60 dark:bg-neutral-900/40 backdrop-blur-sm p-4 shadow-[0_4px_16px_-10px_rgba(15,23,42,0.18)] hover:shadow-[0_8px_24px_-12px_rgba(13,148,136,0.35)] transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <PackageIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
                          {row.product_name}
                        </p>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 mono-num">
                        Ordered: {row.ordered} {row.unit} · PO cost: {formatDocAmount(row.unit_cost, currency)}
                        {row.net_unit_cost !== row.unit_cost &&
                          ` · after discount: ${formatDocAmount(row.net_unit_cost, currency)}`}
                      </p>
                    </div>
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mono-num">
                      Remaining: {fmtQty(row.remaining)} {row.unit}
                    </span>
                  </div>

                  <ReceiveProgressBar
                    ordered={row.ordered}
                    received={row.already_received}
                    receiving={qty}
                  />

                  {/* Qty · cost · tracking capture (serials / batch) sit inline; notes span below. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                    <div>
                      <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                        Qty Received
                      </label>
                      <Input
                        type="number"
                        value={row.quantity_to_receive}
                        onChange={(v) => updateRow(row.po_item_id, 'quantity_to_receive', v)}
                        placeholder={`Remaining ${fmtQty(row.remaining)}`}
                      />
                      {overBy > 0 && (
                        <p className="text-[11px] text-warning-700 dark:text-warning-300 mt-0.5">
                          Receiving {fmtQty(overBy)} {row.unit} over the ordered quantity — accepted as vendor extras.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                        Actual Unit Cost ({docCurrencySymbol(currency)})
                      </label>
                      <Input
                        type="number"
                        value={row.actual_unit_cost}
                        onChange={(v) => updateRow(row.po_item_id, 'actual_unit_cost', v)}
                        placeholder={(row.net_unit_cost / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency))}
                      />
                      {costErr && (
                        <p className="text-[11px] text-danger-600 mt-0.5">{costErr}</p>
                      )}
                      {isForeignCurrency && !costErr && (() => {
                        const typed = parseFloat(row.actual_unit_cost);
                        const tenantCost = Number.isFinite(typed)
                          ? toTenantAmount(Math.round(typed * getSubunitMultiplier(currency)), currency, tenantCurrency, exchangeRate)
                          : null;
                        return tenantCost != null ? (
                          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 tabular-nums">
                            ≈ {formatDocAmount(tenantCost, tenantCurrency)} stock cost
                          </p>
                        ) : null;
                      })()}
                    </div>
                    {row.tracking === 'BATCH' && (
                      <div>
                        <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                          Batch / Lot # · Expiry
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={row.batch_number}
                            onChange={(v) => updateRow(row.po_item_id, 'batch_number', v)}
                            placeholder="Optional"
                            aria-label={`Batch number for ${row.product_name}`}
                          />
                          <Input
                            type="date"
                            value={row.expiry_date}
                            onChange={(v) => updateRow(row.po_item_id, 'expiry_date', v)}
                            aria-label={`Expiry date for ${row.product_name}`}
                          />
                        </div>
                        {batchErr && (
                          <p className="text-[11px] text-danger-600 mt-0.5">{batchErr}</p>
                        )}
                      </div>
                    )}
                    {row.tracking === 'SERIAL' && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                            Serial Numbers
                          </label>
                          {qty > 0 && (
                            <span
                              className={`text-[11px] font-semibold tabular-nums rounded-full px-2 py-0.5 ${
                                serialCount === qty && !serialErr
                                  ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                                  : 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
                              }`}
                            >
                              {serialCount} / {qty} entered
                            </span>
                          )}
                        </div>
                        <TextArea
                          value={row.serials_text}
                          onChange={(v) => updateRow(row.po_item_id, 'serials_text', v)}
                          rows={Math.min(4, Math.max(1, Math.ceil(qty / 3)))}
                          placeholder="Scan or paste serials (comma / newline)"
                          resize="vertical"
                          aria-label={`Serial numbers for ${row.product_name}`}
                        />
                        {serialErr && (
                          <p className="text-[11px] text-danger-600 mt-0.5">{serialErr}</p>
                        )}
                      </div>
                    )}
                    <div className="col-span-full">
                      <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                        Notes
                      </label>
                      <Input
                        value={row.notes}
                        onChange={(v) => updateRow(row.po_item_id, 'notes', v)}
                        placeholder="e.g. Seal intact, packaging good"
                      />
                    </div>
                    {substitutes[row.po_item_id] ? (
                      <SubstituteEditor
                        originalName={row.product_name}
                        value={substitutes[row.po_item_id]}
                        onChange={(next) => setSubstitutes((prev) => ({ ...prev, [row.po_item_id]: next }))}
                        symbol={docCurrencySymbol(currency)}
                      />
                    ) : null}
                    <div className="col-span-full">
                      <button
                        type="button"
                        className="text-xs font-medium text-violet-600 hover:underline dark:text-violet-400"
                        onClick={() => setSubstitutes((prev) => {
                          const next = { ...prev };
                          if (next[row.po_item_id]) delete next[row.po_item_id];
                          else next[row.po_item_id] = emptySubstitute();
                          return next;
                        })}
                      >
                        {substitutes[row.po_item_id] ? 'Remove substitute' : 'Received something else instead?'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Vendor invoice (optional) — number, date and the document itself */}
        {rows.length > 0 && (
          <div className="mt-8 rounded-xl border border-dashed border-orange-300 dark:border-orange-700 bg-orange-50/40 dark:bg-orange-900/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Vendor invoice <span className="font-normal text-neutral-500">(optional)</span></h3>
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Kept with this receipt · raise a Vendor Bill from the PO page when ready</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Invoice #</label>
                <Input value={invoiceNumber} onChange={(v) => setInvoiceNumber(v)} placeholder="INV-77" />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Invoice date</label>
                <Input type="date" value={invoiceDate} onChange={(v) => setInvoiceDate(v)} />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Invoice file (PDF / image, ≤10 MB)</label>
                <FileUpload variant="button" accept={DOCUMENT_ACCEPT} maxSize={DOCUMENT_MAX_BYTES} preview={invoiceFile?.name ?? null} onUpload={(files: File[]) => setInvoiceFile(files[0] ?? null)} />
                {invoiceFile && <p className="text-[11px] text-success-700 dark:text-success-300 mt-1">{invoiceFile.name} selected — uploaded when you confirm the receipt.</p>}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-8 pt-6 border-t border-neutral-200/70 dark:border-neutral-700/50">
          <Button variant="secondary" onClick={() => navigate(`/inventory/purchase-orders/${id}`)}>
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
            Back to PO
          </Button>
          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <CalendarIcon className="h-3.5 w-3.5" />
            <span className="mono-num">Ctrl+Enter to confirm · Esc to cancel</span>
          </div>
          <Button
            variant="primary"
            onClick={() => { void handleSubmit(); }}
            disabled={receiveMutation.isPending || uploading || !hasValidItems || hasErrors}
          >
            <CheckIcon className="h-4 w-4 mr-1.5" />
            {uploading ? 'Uploading invoice…' : receiveMutation.isPending ? 'Receiving...' : 'Confirm Receipt'}
          </Button>
        </div>
      </div>
      {leaveDialog}
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function PurchaseOrderReceivePage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Receive Goods"
      breadcrumbs={[
        { label: 'Purchase' },
        { label: 'Purchase Orders', href: '/purchase-orders' },
        { label: id?.slice(0, 8).toUpperCase() ?? '', href: `/inventory/purchase-orders/${id}` },
        { label: 'Receive' },
      ]}
    >
      <ErrorBoundary>
        <PurchaseOrderReceiveContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
